import { useEffect, useMemo, useRef, useState } from 'react';
import { IoIosRepeat, IoMdArrowDown, IoMdArrowUp, IoMdCheckmarkCircleOutline, IoMdClose, IoMdSearch } from 'react-icons/io';
import { IoChevronDown, IoChevronForward } from 'react-icons/io5';
import type { PriceType } from '../Types/Prices';
import { formatDate } from '../utils/formatDate';
import { normalizeSearch } from '../utils/normalizeSearch';

type PriceDetailsModalProps = {
    price: PriceType;
    onClose: () => void;
    onApprove: (price: PriceType) => Promise<void>;
    isApproving: boolean;
};

function formatPrice(value: number): string {
    const amount = new Intl.NumberFormat('tr-TR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value);
    return `${amount}₺`;
}

function formatChangeRate(value: number): string {
    const rate = new Intl.NumberFormat('tr-TR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value);
    return `${value > 0 ? '+' : ''}${rate}%`;
}

function hasPriceChanged(oldPrice: number, newPrice: number): boolean {
    return Number(oldPrice.toFixed(2)) !== Number(newPrice.toFixed(2));
}

function hasPriceDecreased(oldPrice: number, newPrice: number): boolean {
    return Number(newPrice.toFixed(2)) < Number(oldPrice.toFixed(2));
}

function hasPriceIncreased(oldPrice: number, newPrice: number): boolean {
    return Number(newPrice.toFixed(2)) > Number(oldPrice.toFixed(2));
}

function formatTime(value: number): string {
    if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
    const hours = Math.floor(value);
    const minutes = Math.round((value - hours) * 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

function PriceDetailsModal({ price, onClose, onApprove, isApproving }: PriceDetailsModalProps) {
    const dialogRef = useRef<HTMLElement>(null);
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => new Set(
        price.ProductList.map((item) => JSON.stringify([item.ProductCode, item.OldPrice, item.NewPrice])),
    ));
    const [searchText, setSearchText] = useState('');
    const [changedPricesOnly, setChangedPricesOnly] = useState(true);

    const productGroups = useMemo(() => {
        const groups = new Map<string, {
            id: string;
            productCode: string;
            productName: string;
            oldPrice: number;
            newPrice: number;
            changeRate: number;
            stores: string[];
        }>();

        price.ProductList.forEach((item) => {
            const id = JSON.stringify([item.ProductCode, item.OldPrice, item.NewPrice]);
            const group = groups.get(id);
            if (group) {
                if (!group.stores.includes(item.StoreName)) group.stores.push(item.StoreName);
                return;
            }

            groups.set(id, {
                id,
                productCode: item.ProductCode,
                productName: item.ProductName,
                oldPrice: item.OldPrice,
                newPrice: item.NewPrice,
                changeRate: item.ChangeRate,
                stores: [item.StoreName],
            });
        });

        return Array.from(groups.values());
    }, [price.ProductList]);

    const normalizedSearch = normalizeSearch(searchText);
    const visibleProductGroups = productGroups.filter((group) => {
        if (changedPricesOnly && !hasPriceChanged(group.oldPrice, group.newPrice)) return false;
        if (!normalizedSearch) return true;

        return normalizeSearch(group.productName).includes(normalizedSearch)
            || normalizeSearch(group.productCode).includes(normalizedSearch)
            || group.stores.some((store) => normalizeSearch(store).includes(normalizedSearch));
    });

    const toggleGroup = (groupId: string) => {
        setExpandedGroups((current) => {
            const next = new Set(current);
            if (next.has(groupId)) next.delete(groupId);
            else next.add(groupId);
            return next;
        });
    };

    useEffect(() => {
        const previousOverflow = document.body.style.overflow;
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
                return;
            }

            if (event.key === 'Tab') {
                const focusableElements = dialogRef.current?.querySelectorAll<HTMLElement>(
                    'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
                );
                if (!focusableElements?.length) return;

                const firstElement = focusableElements[0];
                const lastElement = focusableElements[focusableElements.length - 1];
                if (event.shiftKey && document.activeElement === firstElement) {
                    event.preventDefault();
                    lastElement.focus();
                } else if (!event.shiftKey && document.activeElement === lastElement) {
                    event.preventDefault();
                    firstElement.focus();
                }
            }
        };

        document.body.style.overflow = 'hidden';
        closeButtonRef.current?.focus();
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose]);

    return (
        <section
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="price-details-title"
            className="fixed inset-0 z-[1000] flex h-screen h-[100dvh] flex-col overflow-hidden bg-slate-950 text-slate-100"
        >
            <header className="flex shrink-0 items-center justify-between gap-4 border-b border-slate-800 bg-slate-950 px-4 py-3 sm:px-6 lg:px-8">
                <div className="min-w-0">
                    <p className="text-xs font-medium uppercase tracking-[0.16em] text-cyan-300">Proforma fiyat onayı</p>
                    <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                        <h2 id="price-details-title" className="text-lg font-semibold sm:text-xl">Evrak detayları</h2>
                        <span className="text-sm font-medium text-slate-400">{price.Serial}-{price.Number}</span>
                    </div>
                </div>
                <button
                    ref={closeButtonRef}
                    type="button"
                    onClick={onClose}
                    aria-label="Detayları kapat"
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-700 bg-slate-900 text-slate-300 transition-colors hover:border-slate-600 hover:bg-slate-800 hover:text-white focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-cyan-300/30"
                >
                    <IoMdClose aria-hidden="true" size={22} />
                </button>
            </header>

            <div className="grid shrink-0 grid-cols-2 gap-2 border-b border-slate-800 bg-slate-900/60 px-4 py-2 sm:gap-3 sm:px-6 lg:px-8">
                <div className="min-w-0 rounded-lg border border-slate-800 bg-slate-950/70 px-3 py-1.5">
                    <p className="text-[10px] text-slate-400">Oluşturan</p>
                    <p className="break-words text-xs font-medium leading-4 text-slate-100">{price.CreateUser}</p>
                </div>
                <div className="min-w-0 rounded-lg border border-slate-800 bg-slate-950/70 px-2.5 py-1.5">
                    <p className="text-[10px] text-slate-400">Fiyat değişim tarihi</p>
                    <p className="mt-0.5 flex flex-wrap items-baseline gap-x-2 leading-4">
                        <span className="text-xs font-medium">{formatDate(price.ChangedDate)}</span>
                        <span className="text-[10px] text-slate-400">{formatTime(price.ChangedHour)}</span>
                    </p>
                </div>
            </div>

            <div className="grid shrink-0 grid-cols-3 gap-2 border-b border-slate-800 bg-slate-900/60 px-4 py-2 sm:gap-3 sm:px-6 lg:px-8">
                <div className="rounded-lg border border-slate-800 bg-slate-950/70 px-2.5 py-1.5">
                    <p className="text-[10px] text-slate-400">Ürün</p>
                    <p className="mt-0.5 text-base font-semibold leading-5">{price.ProductCount}</p>
                </div>
                <div className="rounded-lg border border-slate-800 bg-slate-950/70 px-2.5 py-1.5">
                    <p className="text-[10px] text-slate-400">Şube</p>
                    <p className="mt-0.5 text-base font-semibold leading-5">{price.StoreCount}</p>
                </div>
                <div className="rounded-lg border border-slate-800 bg-slate-950/70 px-2.5 py-1.5">
                    <p className="text-[10px] text-slate-400">Fiyat satırı</p>
                    <p className="mt-0.5 text-base font-semibold leading-5 text-cyan-300">{price.ProductList.length}</p>
                </div>
            </div>

            <main className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 py-4 sm:px-6 lg:px-8">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                    <h3 className="text-sm font-semibold text-slate-200">Ürün fiyatları</h3>
                    <label className="inline-flex cursor-pointer items-center gap-2 text-xs text-slate-300">
                        <input
                            type="checkbox"
                            checked={changedPricesOnly}
                            onChange={(event) => setChangedPricesOnly(event.target.checked)}
                            className="h-4 w-4 cursor-pointer rounded border-slate-600 bg-slate-900 text-cyan-400 accent-cyan-400 focus:ring-2 focus:ring-cyan-400/30"
                        />
                        <span>Sadece fiyatı değişenleri göster</span>
                    </label>
                </div>
                <label className="relative mb-3 block shrink-0">
                    <span className="sr-only">Ürün veya mağaza ara</span>
                    <IoMdSearch aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input
                        type="search"
                        value={searchText}
                        onChange={(event) => {
                            const value = event.target.value;
                            const query = normalizeSearch(value);
                            setSearchText(value);
                            if (!query) return;

                            setExpandedGroups((current) => {
                                const next = new Set(current);
                                productGroups.forEach((group) => {
                                    const matches = normalizeSearch(group.productName).includes(query)
                                        || normalizeSearch(group.productCode).includes(query)
                                        || group.stores.some((store) => normalizeSearch(store).includes(query));
                                    if (matches) next.add(group.id);
                                });
                                return next;
                            });
                        }}
                        placeholder="Ürün veya mağaza adıyla ara"
                        className="h-10 w-full rounded-xl border border-slate-700 bg-slate-950/80 pl-10 pr-3 text-sm text-slate-100 placeholder:text-slate-500 outline-none transition-colors focus:border-cyan-400 focus:ring-4 focus:ring-cyan-400/15"
                    />
                </label>
                <div className="scrollbar-themed min-h-0 flex-1 overflow-auto rounded-xl border border-slate-800">
                    <div className="grid grid-cols-1 gap-3 p-3 xl:grid-cols-2">
                        {visibleProductGroups.map((group, index) => {
                            const isExpanded = expandedGroups.has(group.id);
                            const storesId = `stores-${index}`;
                            const productMatchesSearch = normalizedSearch.length > 0 && (
                                normalizeSearch(group.productName).includes(normalizedSearch)
                                || normalizeSearch(group.productCode).includes(normalizedSearch)
                            );
                            const visibleStores = normalizedSearch && !productMatchesSearch
                                ? group.stores.filter((store) => normalizeSearch(store).includes(normalizedSearch))
                                : group.stores;
                            const decreased = hasPriceDecreased(group.oldPrice, group.newPrice);
                            const increased = hasPriceIncreased(group.oldPrice, group.newPrice);
                            return (
                                <article
                                    key={group.id}
                                    className={`overflow-hidden rounded-xl border bg-slate-950 shadow-sm transition-colors ${decreased
                                        ? 'border-rose-500/80 shadow-rose-950/20 hover:border-rose-400'
                                        : 'border-slate-800 shadow-black/20 hover:border-slate-700'}`}
                                >
                                    <button
                                        type="button"
                                        onClick={() => toggleGroup(group.id)}
                                        aria-expanded={isExpanded}
                                        aria-controls={storesId}
                                        className="block w-full p-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cyan-300"
                                    >
                                        <span className="flex items-start justify-between gap-3">
                                            <span className="flex min-w-0 items-start gap-2">
                                                {isExpanded
                                                    ? <IoChevronDown className="mt-0.5 shrink-0 text-cyan-300" aria-hidden="true" />
                                                    : <IoChevronForward className="mt-0.5 shrink-0 text-cyan-300" aria-hidden="true" />}
                                                <span className="break-words font-semibold leading-5 text-slate-100">{group.productName}</span>
                                            </span>
                                            <span className="shrink-0 rounded-full border border-cyan-900/80 bg-cyan-950/50 px-2.5 py-1 text-xs font-medium text-cyan-200">
                                                {group.stores.length} mağaza
                                            </span>
                                        </span>
                                        <span className="mt-4 grid grid-cols-3 gap-2 border-t border-slate-800 pt-3">
                                            <span className="min-w-0">
                                                <span className="block text-[11px] text-slate-400">Önceki fiyat</span>
                                                <span className="mt-1 block whitespace-nowrap text-sm tabular-nums text-slate-300">{formatPrice(group.oldPrice)}</span>
                                            </span>
                                            <span className="min-w-0 text-center">
                                                <span className="block text-[11px] text-slate-400">Yeni fiyat</span>
                                                <span className="mt-1 block whitespace-nowrap text-sm font-semibold tabular-nums text-slate-100">{formatPrice(group.newPrice)}</span>
                                            </span>
                                            <span className="min-w-0 text-right">
                                                <span className={`inline-flex items-center justify-end gap-1 text-[11px] font-medium ${decreased ? 'text-rose-300' : increased ? 'text-emerald-300' : 'text-slate-400'}`}>
                                                    {decreased && <IoMdArrowDown aria-hidden="true" size={13} />}
                                                    {increased && <IoMdArrowUp aria-hidden="true" size={13} />}
                                                    {decreased ? 'Fiyat düşüşü' : increased ? 'Fiyat artışı' : 'Değişim oranı'}
                                                </span>
                                                <span className={`mt-1 block whitespace-nowrap text-sm font-semibold tabular-nums ${decreased ? 'text-rose-300' : increased ? 'text-emerald-300' : 'text-slate-300'}`}>
                                                    {formatChangeRate(group.changeRate)}
                                                </span>
                                            </span>
                                        </span>
                                    </button>
                                    {isExpanded && (
                                        <div id={storesId} className="border-t border-slate-800 bg-slate-900/80 px-4 py-3">
                                            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                                                {normalizedSearch && !productMatchesSearch
                                                    ? `Eşleşen mağazalar (${visibleStores.length}/${group.stores.length})`
                                                    : 'Bu fiyatı uygulayan mağazalar'}
                                            </p>
                                            <ul className="flex flex-wrap gap-2">
                                                {visibleStores.map((store) => (
                                                    <li key={store} className="rounded-lg border border-slate-700 bg-slate-800 px-2.5 py-1 text-xs text-slate-200">{store}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </article>
                            );
                        })}
                        {visibleProductGroups.length === 0 && (
                            <div className="col-span-full rounded-xl border border-dashed border-slate-700 px-5 py-10 text-center">
                                <p className="font-medium text-slate-200">
                                    {normalizedSearch ? 'Eşleşen ürün veya mağaza bulunamadı' : 'Fiyatı değişen ürün bulunamadı'}
                                </p>
                                <p className="mt-1 text-sm text-slate-400">
                                    {normalizedSearch ? 'Arama metnini değiştirip yeniden deneyin.' : 'Tüm fiyat gruplarını görmek için fiyat değişikliği filtresini kapatın.'}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </main>

            <footer className="flex shrink-0 flex-col-reverse items-stretch justify-between gap-3 border-t border-slate-800 bg-slate-950 px-4 py-3 sm:flex-row sm:items-center sm:px-6 lg:px-8">
                <p className="text-xs text-slate-400"><span className="font-semibold text-slate-200">{productGroups.length}</span> fiyat grubu · <span className="font-semibold text-slate-200">{price.ProductList.length}</span> mağaza fiyatı</p>
                <div className="flex flex-col-reverse gap-2 sm:flex-row">
                    <button type="button" onClick={onClose} className="rounded-xl border border-slate-700 bg-slate-900 px-5 py-2.5 text-sm font-semibold text-slate-200 transition-colors hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-cyan-300/30">
                        Kapat
                    </button>
                    <button type="button" disabled={isApproving} onClick={() => onApprove(price)} className="inline-flex items-center justify-center gap-2 rounded-xl bg-cyan-400 px-5 py-2.5 text-sm font-semibold text-slate-950 transition-colors hover:bg-cyan-300 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-cyan-300/30 disabled:cursor-wait disabled:opacity-70">
                        {isApproving ? <IoIosRepeat aria-hidden="true" className="animate-spin" size={18} /> : <IoMdCheckmarkCircleOutline aria-hidden="true" size={18} />}
                        {isApproving ? 'Onaylanıyor…' : 'Bu evrağı onayla'}
                    </button>
                </div>
            </footer>
        </section>
    );
}

export default PriceDetailsModal;
